import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navbar, Nav, Container, Dropdown, Badge, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../store/GlobalContext';
import axios from '../../api/axios';
import { useI18n } from '../../i18n/I18nContext';
import { getGuestCart, GuestCartItem } from '../../utils/guestCart';

type HeaderCartItem = {
    productId: string;
    name: string;
    quantity: number;
    unitPrice: number;
    subtotal: number;
};

type HeaderCartPayload = {
    totalItems: number;
    totalAmount: number;
    items: HeaderCartItem[];
};

type HeaderNotificationItem = {
    _id: string;
    title: string;
    message: string;
    isRead: boolean;
    createdAt: string;
};

const Header = () => {
    const navigate = useNavigate();
    const { state, logout, setCartCount } = useContext(GlobalContext);
    const { t } = useI18n();
    const [cart, setCart] = useState<HeaderCartPayload>({ totalItems: 0, totalAmount: 0, items: [] });
    const [guestCartItems, setGuestCartItems] = useState<GuestCartItem[]>([]);
    const [loadingCart, setLoadingCart] = useState(false);
    const [notificationItems, setNotificationItems] = useState<HeaderNotificationItem[]>([]);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const userRoles: string[] = Array.isArray(state?.auth?.user?.roles) ? state.auth.user.roles : [];
    const isAdminUser = userRoles.includes('store_admin') || userRoles.includes('super_admin');
    const isLoggedIn = Boolean(state?.auth?.token);
    const user = state?.auth?.user;
    const globalCartCount = Number(state?.cartCount ?? 0);

    const displayName = useMemo(() => {
        if (!user) return 'Guest';
        if (user.firstName) return user.firstName;
        if (user.username) return user.username;
        if (user.email) return user.email.split('@')[0];
        return 'Customer';
    }, [user]);

    const fetchMiniCart = useCallback(async () => {
        if (!state?.auth?.token || isAdminUser) {
            setCart({ totalItems: 0, totalAmount: 0, items: [] });
            setCartCount(0);
            return;
        }

        setLoadingCart(true);
        try {
            const response = await axios.get('/cart/my', {
                headers: { Authorization: `Bearer ${state.auth.token}` },
            });
            const payload = response.data || {};
            setCart({
                totalItems: Number(payload.totalItems ?? 0),
                totalAmount: Number(payload.totalAmount ?? 0),
                items: Array.isArray(payload.items) ? payload.items : [],
            });
            setCartCount(Number(payload.totalItems ?? 0));
        } catch {
            setCart({ totalItems: 0, totalAmount: 0, items: [] });
            setCartCount(0);
        } finally {
            setLoadingCart(false);
        }
    }, [state?.auth?.token, isAdminUser, setCartCount]);

    const fetchUnreadNotificationCount = useCallback(async () => {
        if (!state?.auth?.token) {
            setUnreadNotifications(0);
            return;
        }

        try {
            const response = await axios.get('/notifications/my/unread-count', {
                headers: { Authorization: `Bearer ${state.auth.token}` },
            });
            setUnreadNotifications(Number(response.data?.unreadCount ?? 0));
        } catch {
            setUnreadNotifications(0);
        }
    }, [state?.auth?.token]);

    const fetchNotificationPreview = useCallback(async () => {
        if (!state?.auth?.token) {
            setNotificationItems([]);
            setUnreadNotifications(0);
            return;
        }

        setLoadingNotifications(true);
        try {
            const response = await axios.get('/notifications/my', {
                headers: { Authorization: `Bearer ${state.auth.token}` },
                params: { page: 1, limit: 5 },
            });
            const list = Array.isArray(response.data?.notifications) ? response.data.notifications : [];
            setNotificationItems(list);
            setUnreadNotifications(Number(response.data?.unreadCount ?? 0));
        } catch {
            setNotificationItems([]);
        } finally {
            setLoadingNotifications(false);
        }
    }, [state?.auth?.token]);

    const markNotificationRead = useCallback(async (notificationId: string) => {
        if (!state?.auth?.token) return;
        try {
            await axios.put(`/notifications/my/${notificationId}/read`, {}, {
                headers: { Authorization: `Bearer ${state.auth.token}` },
            });
            setNotificationItems((prev) => prev.map((item) => item._id === notificationId ? { ...item, isRead: true } : item));
            setUnreadNotifications((prev) => Math.max(prev - 1, 0));
        } catch {
            // Ignore preview mark-read failures to keep the header non-blocking.
        }
    }, [state?.auth?.token]);

    const markAllNotificationsRead = useCallback(async () => {
        if (!state?.auth?.token) return;
        try {
            await axios.put('/notifications/my/read-all', {}, {
                headers: { Authorization: `Bearer ${state.auth.token}` },
            });
            setNotificationItems((prev) => prev.map((item) => ({ ...item, isRead: true })));
            setUnreadNotifications(0);
        } catch {
            // Ignore preview mark-all failures to avoid blocking navigation.
        }
    }, [state?.auth?.token]);

    useEffect(() => {
        if (isLoggedIn) {
            fetchMiniCart();
        } else {
            const items = getGuestCart();
            setGuestCartItems(items);
            setCartCount(items.reduce((s, i) => s + i.quantity, 0));
        }
    }, [fetchMiniCart, isLoggedIn, setCartCount]);

    useEffect(() => {
        fetchUnreadNotificationCount();
    }, [fetchUnreadNotificationCount]);

    const handleCartToggle = (isOpen: boolean) => {
        if (isOpen) {
            fetchMiniCart();
        }
    };

    const handleNotificationsToggle = (isOpen: boolean) => {
        if (isOpen) {
            fetchNotificationPreview();
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <header style={{ position: 'sticky', top: 0, zIndex: 1030, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            {/* ── Arkab top utility bar ── */}
            <div style={{
                backgroundColor: 'var(--arkab-dark)',
                color: '#ffffff',
                fontSize: '0.8rem',
                letterSpacing: '0.06em',
                fontFamily: 'var(--arkab-font-body)',
            }}>
                <div style={{ maxWidth: '1600px', width: '100%', margin: '0 auto', padding: '0 clamp(12px, 2.2vw, 28px)', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Logo icon + brand mark */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
                        <img
                            src="/arkab/WhatsApp-20Image-202026-05-14-20at-205_51_53-20PM.jpeg"
                            alt="Arkab"
                            style={{ height: '24px', width: '24px', objectFit: 'cover', borderRadius: '3px' }}
                        />
                        <span style={{ fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.12em' }}>ARKAB</span>
                    </div>

                    {/* Utility nav + phone */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <nav style={{ display: 'flex', gap: '0.9rem' }} className="d-none d-md-flex">
                            <span style={{ cursor: 'pointer', opacity: 0.85, transition: 'opacity 0.2s' }} onMouseEnter={e => (e.currentTarget.style.opacity='1')} onMouseLeave={e => (e.currentTarget.style.opacity='0.85')} onClick={() => navigate('/products')}>Products</span>
                            <span style={{ cursor: 'pointer', opacity: 0.85, transition: 'opacity 0.2s' }} onMouseEnter={e => (e.currentTarget.style.opacity='1')} onMouseLeave={e => (e.currentTarget.style.opacity='0.85')} onClick={() => navigate('/contact')}>Contact</span>
                            {isAdminUser && <span style={{ cursor: 'pointer', opacity: 0.85, transition: 'opacity 0.2s' }} onMouseEnter={e => (e.currentTarget.style.opacity='1')} onMouseLeave={e => (e.currentTarget.style.opacity='0.85')} onClick={() => navigate('/admin')}>{t('header_admin_panel')}</span>}
                        </nav>
                        <span className="d-none d-lg-inline" style={{ opacity: 0.7, fontSize: '0.78rem' }}>📞 0371-1944889</span>
                    </div>
                </div>
            </div>

            {/* ── Arkab main brand bar ── */}
            <Navbar bg="white" expand="lg" className="app-navbar" style={{
                borderBottom: '1px solid var(--app-border)',
                padding: '0',
            }}>
                <Container fluid className="w-100" style={{ maxWidth: '1600px', paddingLeft: 'clamp(12px, 2.2vw, 28px)', paddingRight: 'clamp(12px, 2.2vw, 28px)' }}>
                    {/* Brand – large "ARKAB COLLECTIONS" */}
                    <Navbar.Brand
                        onClick={() => navigate('/')}
                        style={{
                            cursor: 'pointer',
                            fontFamily: 'var(--arkab-font-heading)',
                            fontWeight: 800,
                            fontSize: 'clamp(1.1rem, 2.5vw, 1.75rem)',
                            letterSpacing: '0.08em',
                            color: 'var(--arkab-dark)',
                            textTransform: 'uppercase',
                            padding: '0.65rem 0',
                            lineHeight: 1,
                        }}
                    >
                        ARKAB COLLECTIONS
                    </Navbar.Brand>

                    {/* Mobile toggle */}
                    <Navbar.Toggle aria-controls="navbar-nav" style={{ border: '1px solid var(--app-border)', padding: '0.3rem 0.6rem' }} />

                    <Navbar.Collapse id="navbar-nav">
                        {/* Mobile nav links */}
                        <Nav className="me-auto d-lg-none py-2">
                            <Nav.Link onClick={() => navigate('/')} style={{ fontWeight: 600, letterSpacing: '0.04em' }}>{t('header_home')}</Nav.Link>
                            <Nav.Link onClick={() => navigate('/collections')} style={{ fontWeight: 600, letterSpacing: '0.04em' }}>{t('header_catalog')}</Nav.Link>
                            <Nav.Link onClick={() => navigate('/contact')} style={{ fontWeight: 600, letterSpacing: '0.04em' }}>{t('header_contact')}</Nav.Link>
                            {isAdminUser && <Nav.Link onClick={() => navigate('/admin')} style={{ fontWeight: 600, letterSpacing: '0.04em' }}>{t('header_admin_panel')}</Nav.Link>}
                        </Nav>

                        {/* Desktop spacer */}
                        <div className="d-none d-lg-block" style={{ flex: 1 }} />

                        <Nav className="align-items-center gap-2">
                            {isLoggedIn ? (
                                <Dropdown align="end" className="me-2">
                                    <Dropdown.Toggle variant="outline-dark" size="sm" id="header-user-menu">
                                        {displayName}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => navigate('/account')}>{t('header_my_account')}</Dropdown.Item>
                                        {!isAdminUser && <Dropdown.Item onClick={() => navigate('/account/orders')}>{t('header_my_orders')}</Dropdown.Item>}
                                        {!isAdminUser && <Dropdown.Item onClick={() => navigate('/account/favourites')}>{t('header_my_favourites')}</Dropdown.Item>}
                                        {isAdminUser && <Dropdown.Item onClick={() => navigate('/admin')}>{t('header_admin_panel')}</Dropdown.Item>}
                                        <Dropdown.Divider />
                                        <Dropdown.Item onClick={handleLogout}>{t('common_logout')}</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            ) : (
                                <Nav.Link onClick={() => navigate('/login')}>{t('header_sign_in')}</Nav.Link>
                            )}

                            {isLoggedIn && (
                                <Dropdown align="end" onToggle={(open) => handleNotificationsToggle(Boolean(open))}>
                                    <Dropdown.Toggle variant="outline-dark" id="header-notifications" size="sm">
                                        🔔
                                        {unreadNotifications > 0 && (
                                            <Badge bg="danger" pill className="ms-1">{unreadNotifications}</Badge>
                                        )}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu style={{ minWidth: 360, padding: '0.75rem' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>{t('notifications_title')}</strong>
                                            {unreadNotifications > 0 ? (
                                                <Button variant="link" size="sm" className="p-0" onClick={markAllNotificationsRead}>
                                                    {t('notifications_mark_all_read')}
                                                </Button>
                                            ) : null}
                                        </div>

                                        {loadingNotifications ? (
                                            <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
                                        ) : notificationItems.length === 0 ? (
                                            <p className="mb-2 text-muted">{t('notifications_empty')}</p>
                                        ) : (
                                            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                                                {notificationItems.map((item) => (
                                                    <button
                                                        key={item._id}
                                                        type="button"
                                                        className="w-100 border-0 bg-transparent text-start p-2 rounded mb-1"
                                                        style={{
                                                            background: item.isRead ? 'transparent' : '#f8f9fa',
                                                        }}
                                                        onClick={async () => {
                                                            if (!item.isRead) {
                                                                await markNotificationRead(item._id);
                                                            }
                                                            navigate('/account/notifications');
                                                        }}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                                            <div>
                                                                <div className="fw-semibold">{item.title}</div>
                                                                <small className="text-muted d-block">{item.message}</small>
                                                            </div>
                                                            {!item.isRead ? <Badge bg="primary">{t('notifications_new')}</Badge> : null}
                                                        </div>
                                                        <small className="text-muted">{new Date(item.createdAt).toLocaleString()}</small>
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        <hr className="my-2" />
                                        <Button
                                            className="w-100"
                                            variant="dark"
                                            size="sm"
                                            onClick={() => navigate('/account/notifications')}
                                        >
                                            {t('notifications_view_all')}
                                        </Button>
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}

                            {!isAdminUser && (
                                <Dropdown align="end" onToggle={(open) => { if (open && isLoggedIn) handleCartToggle(true); if (open && !isLoggedIn) setGuestCartItems(getGuestCart()); }}>
                                    <Dropdown.Toggle variant="outline-dark" id="header-mini-cart" size="sm">
                                        🛒
                                        {globalCartCount > 0 && (
                                            <Badge bg="danger" pill className="ms-1">{globalCartCount}</Badge>
                                        )}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu style={{ minWidth: 320, padding: '0.75rem' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>{t('header_my_cart')}</strong>
                                            <small>{globalCartCount} item(s)</small>
                                        </div>

                                        {isLoggedIn ? (
                                            loadingCart ? (
                                                <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
                                            ) : cart.items.length === 0 ? (
                                                <p className="mb-2">{t('header_cart_empty')}</p>
                                            ) : (
                                                <>
                                                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                                        {cart.items.slice(0, 4).map((item) => (
                                                            <div key={item.productId} className="d-flex justify-content-between mb-2">
                                                                <div style={{ maxWidth: '70%' }}>
                                                                    <div className="fw-semibold text-truncate">{item.name}</div>
                                                                    <small>Qty {item.quantity}</small>
                                                                </div>
                                                                <small>Rs {Number(item.subtotal).toFixed(2)}</small>
                                                            </div>
                                                        ))}
                                                        {cart.items.length > 4 && (
                                                            <small className="text-muted">+{cart.items.length - 4} more item(s)</small>
                                                        )}
                                                    </div>
                                                    <hr className="my-2" />
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <strong>{t('header_total')}</strong>
                                                        <strong>Rs {Number(cart.totalAmount).toFixed(2)}</strong>
                                                    </div>
                                                </>
                                            )
                                        ) : guestCartItems.length === 0 ? (
                                            <p className="mb-2 text-muted" style={{ fontSize: '0.85rem' }}>Your cart is empty.</p>
                                        ) : (
                                            <>
                                                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                                    {guestCartItems.slice(0, 4).map((item) => (
                                                        <div key={item.productId} className="d-flex justify-content-between mb-2">
                                                            <div style={{ maxWidth: '70%' }}>
                                                                <div className="fw-semibold text-truncate">{item.name}</div>
                                                                <small>Qty {item.quantity}</small>
                                                            </div>
                                                            <small>Rs {(item.price * item.quantity).toFixed(0)}</small>
                                                        </div>
                                                    ))}
                                                    {guestCartItems.length > 4 && (
                                                        <small className="text-muted">+{guestCartItems.length - 4} more item(s)</small>
                                                    )}
                                                </div>
                                                <hr className="my-2" />
                                                <div className="d-flex justify-content-between mb-2">
                                                    <strong>Total</strong>
                                                    <strong>Rs {guestCartItems.reduce((s, i) => s + i.price * i.quantity, 0).toFixed(0)}</strong>
                                                </div>
                                            </>
                                        )}

                                        <Button
                                            className="w-100"
                                            variant="dark"
                                            size="sm"
                                            onClick={() => navigate('/checkout')}
                                            disabled={globalCartCount === 0}
                                        >
                                            {t('header_go_checkout')}
                                        </Button>
                                        {!isLoggedIn && (
                                            <div style={{ textAlign: 'center', fontSize: '0.75rem', color: '#888', marginTop: '0.5rem' }}>
                                                <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/login')}>Sign in</span> for order history
                                            </div>
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
        </header>
    )
}

export default Header